import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  Observable,
  Subscription,
  catchError,
  combineLatest,
  filter,
  interval,
  map,
  of,
  race,
  switchMap,
  take,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { AuthService } from '@app/core/services/auth/auth.service';
import { FramesService } from '@app/core/services/frames';
import { APP_CONFIG } from '@app/core/app-config';

/**
 * Cross-app form launch handoff (ERP-15430).
 *
 * V4 launches a V2 form by `window.open`-ing V2's SPA root with `?callForm=`
 * and an optional `?formContext=` JSON blob:
 *
 *   window.open(
 *     'https://uat01.erplinq.com/20_JOEB/?callForm=AMVENINQFMX' +
 *       '&formContext=' + encodeURIComponent('{"vendorId":"123","fiscalYear":"2026"}'),
 *     'LINQ ERP V2');
 *
 * On boot, this service:
 *   1. In `capture()`: reads both query params, strips them from the
 *      address bar so refresh doesn't relaunch, AND — if `formContext`
 *      was present — eagerly fires a fire-and-forget HTTP `GET` to a
 *      lightweight V2 MVC endpoint with `?formContext=` attached. V2's
 *      `GlobalStateFormContextProcessor` MVC filter reads the blob and
 *      writes each entry into the session-scoped `Globals` cache. The
 *      response body is discarded.
 *   2. In `start()` → `dispatch()`: waits for BOTH `authService.framesUser$`
 *      (login completed, USER_INFO present) AND `framesService.ready$`
 *      (Frames `viewready` fired). When both gates open, **synchronously**
 *      calls `Frames.Application.callmenu(...)` — the same call path
 *      V2's own menu service uses for a user-initiated CALL_FORM click.
 *
 * Why the side-channel fires at `capture()` time, not `dispatch()` time:
 * a prior implementation awaited the side-channel response before calling
 * `callmenu`. That introduced a 100–1000ms delay between Frames' viewready
 * and the call, which pushed `callmenu` past the Frames lifecycle window
 * where it actually installs the new task — the call silently no-op'd,
 * no XHR fired, and the form never opened. Eager firing keeps `callmenu`
 * synchronous to readiness (the timing that already works) while still
 * giving the side-channel ~Frames-bootstrap-worth of time to complete
 * before the form's first server-side request reads Globals. In practice
 * Frames bootstrap is much slower than this single lightweight MVC call,
 * so the side-channel typically finishes well before `callmenu` fires.
 *
 * The side-channel call is needed because V4's launch URL navigates to
 * V2's SPA root, which is served by `MapFallbackToFile("index.html")` —
 * that path bypasses the MVC pipeline, so `GlobalStateFormContextProcessor`
 * never sees the original launch query string. Routing the same blob
 * through any MVC endpoint causes the filter to fire and Globals to
 * populate before Frames issues its CALL_FORM request.
 *
 * If either readiness signal never fires (login stalls, Frames never
 * inits, user logs out before workspace loads), the pending launch is
 * silently dropped on app destroy.
 *
 * Replaces the prior server-side bridge (`LaunchFormController`) which
 * served patched SPA HTML with an injected polling script. The bridge was
 * brittle: the `<base href>` rewrite, the `history.replaceState`
 * workaround, and the fingerprint-validation middleware all interacted
 * badly with the cross-app launch flow.
 */
@Injectable({ providedIn: 'root' })
export class PendingLaunchService {
  private readonly authService = inject(AuthService);
  private readonly framesService = inject(FramesService);
  private readonly http = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);

  /**
   * Endpoint used purely as a side-channel to route `formContext` through
   * V2's MVC filter pipeline. Chosen because it's lightweight (in-memory
   * settings lookup, no DB hit), `[AllowAnonymous]` so it works regardless
   * of where in the auth bootstrap we are, and idempotent so an
   * accidental double-call has no effect.
   */
  private static readonly FORM_CONTEXT_SIDE_CHANNEL = 'api/config/app-config.json';

  /**
   * After `framesUser$` + `ready$` both emit, V2's login dialog (an
   * Angular Material CDK overlay) may still be in the DOM for one or two
   * frames while its close animation runs. `Frames.Application.callmenu`
   * silently no-ops while a `.cdk-overlay-pane` is present, so we poll
   * for its removal before dispatching. This mirrors the gate the prior
   * `LaunchFormController` bridge had.
   */
  private static readonly OVERLAY_POLL_MS = 200;

  /**
   * Hard ceiling on how long we'll wait for the overlay to clear before
   * dispatching anyway. The fallback is a defensive escape hatch: if the
   * login dialog truly never closes the user is already in a broken
   * state, but at least we try the form launch rather than silently
   * hanging on the workspace. A console.warn surfaces the situation.
   */
  private static readonly OVERLAY_MAX_WAIT_MS = 30_000;

  /**
   * postMessage envelope type used by V4 to launch a form into THIS
   * already-open V2 tab without a reload. Keep this string in sync with V4's
   * `LaunchV2FormService` (`V2_LAUNCH_MESSAGE_TYPE`).
   */
  private static readonly LAUNCH_MESSAGE_TYPE = 'erp-v2-launch-form';

  /**
   * ACK envelope type posted back to V4 after an in-place launch is dispatched.
   * Only V4's `window.opener` fallback (v2-starter flow) waits for it — it lets
   * V4 tell "the V2 tab handled it in place" apart from "no V2 listener", so it
   * can fall back to a full-reload `window.open` only when truly needed. Keep in
   * sync with V4's `LaunchV2FormService` (`V2_LAUNCH_ACK_TYPE`).
   */
  private static readonly LAUNCH_ACK_TYPE = 'erp-v2-launch-form-ack';

  /** Interval between `callmenu` retries when no new view has rendered yet. */
  private static readonly DISPATCH_RETRY_INTERVAL_MS = 500;

  /**
   * Max `callmenu` attempts (first synchronous try + retries) before giving up.
   * 20 × 500ms ≈ 10s, generous enough to cover a slow first-login workspace
   * transition. Dropped attempts are no-ops, so a high cap is harmless; we stop
   * the instant a `viewready` is observed.
   */
  private static readonly DISPATCH_MAX_ATTEMPTS = 20;

  // Form name to dispatch once gates open. null when there's nothing pending
  // (most app launches don't carry a ?callForm).
  private pendingFormName: string | null = null;
  // Raw URL-decoded JSON blob captured from ?formContext, or null. Lives
  // alongside pendingFormName so a single dispatch handles both.
  private pendingFormContext: string | null = null;
  // One-shot guard so a race on the readiness observables can't double-fire
  // the menu call.
  private dispatched = false;
  private subscription: Subscription | undefined;
  // Subscription for a message-driven launch into this already-open tab.
  // Independent of `subscription` (the boot ?callForm path) so the two can't
  // tear each other down.
  private launchSubscription: Subscription | undefined;
  // Retry loop for a dispatched callmenu that Frames dropped (no CALL_FORM request).
  private retrySubscription: Subscription | undefined;
  // Watches Frames' request stream for THIS form's CALL_FORM request — the
  // signal that the launch actually landed. Torn down when the launch resolves.
  private dispatchRequestSub: Subscription | undefined;

  /**
   * Read `?callForm` (required) and `?formContext` (optional) from the
   * current URL and remember them. Strips both params from the address bar
   * via `history.replaceState` so a page refresh doesn't replay the launch.
   * Returns `true` when a pending launch was captured — useful for tests;
   * production callers can ignore the return.
   *
   * `callForm` validation mirrors the server-side XSS sanitization that
   * used to live in `LaunchFormController`: form names must match
   * `^[A-Za-z0-9_]+$`. Anything else is treated as no-op (no launch, no
   * URL mutation).
   *
   * `formContext` is expected to be a URL-encoded JSON object whose entries
   * are string→string. We do a shallow shape check here so the side-channel
   * call never ships malformed JSON; deeper validation is left to V2's
   * `GlobalStateFormContextProcessor`.
   */
  capture(): boolean {
    const params = new URLSearchParams(window.location.search);
    const callForm = params.get('callForm');
    if (!callForm) {
      return false;
    }
    if (!/^[A-Za-z0-9_]+$/.test(callForm)) {
      // Reject malformed values silently rather than allowing them through
      // to Frames. Matches the server-side validation in LaunchFormController.
      return false;
    }

    this.pendingFormName = callForm;
    this.pendingFormContext = this.sanitizeFormContext(params.get('formContext'));

    params.delete('callForm');
    params.delete('formContext');
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);

    // Eagerly fire the side-channel call so `GlobalStateFormContextProcessor`
    // populates Globals well before Frames is ready. See the class doc for
    // why we must NOT wait for the response before dispatching callmenu.
    if (this.pendingFormContext) {
      this.fireFormContextSideChannel(this.pendingFormContext);
    }

    return true;
  }

  /**
   * Send the JSON blob to a lightweight V2 MVC endpoint so its filter
   * pipeline populates the session-scoped Globals cache. Fire-and-forget:
   * we don't await the response anywhere — the launch flow assumes it
   * lands before Frames issues its first CALL_FORM XHR.
   *
   * Note the interceptor: `linqHttpInterceptor` blocks each outgoing
   * request until `authService.accessToken$` emits. If this is called
   * before login completes, the request just queues until the bearer is
   * available — that's fine, because we still want globals set on the
   * post-login session before Frames asks for the form.
   */
  private fireFormContextSideChannel(formContext: string): void {
    // Boot path: fire-and-forget. We don't await the response — see the
    // class doc for why awaiting before callmenu breaks the boot lifecycle.
    this.formContextSideChannel$(formContext).subscribe();
  }

  /**
   * The side-channel GET as an observable that emits EXACTLY ONCE then
   * completes — `null` on success or on error (error is swallowed). The
   * single guaranteed emission lets the message-driven launch path await it
   * via `switchMap` and still dispatch `callmenu` even when Globals
   * hydration failed.
   */
  private formContextSideChannel$(formContext: string): Observable<unknown> {
    const params = new HttpParams({ fromObject: { formContext } });
    return this.http
      .get(PendingLaunchService.FORM_CONTEXT_SIDE_CHANNEL, {
        params,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((): null => null),
        catchError(() => {
          // eslint-disable-next-line no-console
          console.warn('PendingLaunchService: formContext side-channel call failed');
          return of(null);
        }),
      );
  }

  /**
   * Validate a raw `?formContext`/message `formContext` value: it must be a
   * URL-decoded JSON OBJECT (string→string bag for Globals). Returns the
   * original string when valid, else `null`. Arrays, primitives, `null`, and
   * malformed JSON are all dropped silently — the form still launches, just
   * without pre-populated context.
   */
  private sanitizeFormContext(formContext: string | null | undefined): string | null {
    if (!formContext) {
      return null;
    }
    try {
      const parsed = JSON.parse(formContext);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return formContext;
      }
    } catch {
      // Malformed JSON — drop, proceed without context.
    }
    return null;
  }

  /**
   * Begin watching for login + Frames readiness. Idempotent — calling more
   * than once after a successful capture is a no-op. If no form was
   * captured, returns immediately.
   */
  start(): void {
    if (!this.pendingFormName || this.subscription) {
      return;
    }

    this.subscription = combineLatest([this.authService.framesUser$, this.framesService.ready$])
      .pipe(
        filter(([user, ready]) => Boolean(user) && ready),
        take(1),
        // Third gate: wait for V2's login dialog (.cdk-overlay-pane) to
        // leave the DOM. Frames silently drops callmenu calls while the
        // overlay is present — the symptom is "callmenu fires, no XHR".
        switchMap(() => this.waitForOverlayCleared()),
      )
      .subscribe(() => this.dispatch());
  }

  /**
   * Resolves when the CDK overlay (V2's login dialog) is no longer in
   * the DOM, or when {@link OVERLAY_MAX_WAIT_MS} elapses — whichever
   * comes first. Used to gate `dispatch()` so `Frames.callmenu` lands
   * inside the lifecycle window where it actually installs the form view.
   *
   * Returns synchronously via `of(undefined)` when no overlay is present
   * at the moment of the call — most plain-`?callForm` launches will hit
   * this fast path because the dialog has already torn down by the time
   * both readiness signals fire.
   */
  private waitForOverlayCleared(): Observable<void> {
    if (!document.querySelector('.cdk-overlay-pane')) {
      return of(undefined);
    }
    console.log('PendingLaunchService: CDK overlay still present, waiting for it to clear');

    const cleared$ = interval(PendingLaunchService.OVERLAY_POLL_MS).pipe(
      filter(() => !document.querySelector('.cdk-overlay-pane')),
      take(1),
      map((): void => undefined),
    );

    const timeoutFallback$ = timer(PendingLaunchService.OVERLAY_MAX_WAIT_MS).pipe(
      tap(() =>
        console.warn(
          `PendingLaunchService: CDK overlay still present after ${PendingLaunchService.OVERLAY_MAX_WAIT_MS}ms — dispatching anyway`,
        ),
      ),
      map((): void => undefined),
    );

    return race(cleared$, timeoutFallback$);
  }

  /**
   * Tear down the boot readiness subscription AND the message-driven launch
   * subscription, and stop listening for V4 launch messages. Safe to call when
   * nothing is pending / no listener was added.
   */
  stop(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.launchSubscription?.unsubscribe();
    this.launchSubscription = undefined;
    this.retrySubscription?.unsubscribe();
    this.retrySubscription = undefined;
    this.dispatchRequestSub?.unsubscribe();
    this.dispatchRequestSub = undefined;
    window.removeEventListener('message', this.onMessage);
  }

  /**
   * Begin listening for V4 `postMessage` launch requests into this
   * already-open tab. Idempotent at the DOM level — `addEventListener`
   * dedupes the same bound handler. Pairs with `stop()` which removes it.
   *
   * This is the no-reload counterpart to `capture()`: once V2 is loaded, V4
   * reuses the tab by posting a launch message instead of re-navigating it.
   */
  listen(): void {
    window.addEventListener('message', this.onMessage);
  }

  /**
   * Handler for V4 launch messages. Accepts a message ONLY when:
   *   - `event.origin` equals the configured V4 frontend origin (see
   *     {@link v4FrontendOrigin}) — the message is cross-origin (V4 -> V2), so
   *     we trust V4's origin explicitly; `event.origin` is browser-set and
   *     unspoofable. (We validate origin rather than `window.opener` because a
   *     preceding Auth0 login redirect can sever the opener relationship.); AND
   *   - the envelope `type` matches {@link LAUNCH_MESSAGE_TYPE}; AND
   *   - `callForm` is a string (validated against the XSS-safe regex in
   *     `launchForm`).
   * Anything else is ignored silently.
   */
  private readonly onMessage = (event: MessageEvent): void => {
    // Accept launch requests ONLY from the configured V4 frontend origin. The
    // message is intentionally CROSS-origin (V4 -> V2), so we validate the
    // SENDER's origin against V4's frontend URL (from app-config), NOT V2's own
    // origin. `event.origin` is set by the browser and can't be spoofed.
    const trustedOrigin = this.v4FrontendOrigin();
    if (!trustedOrigin || event.origin !== trustedOrigin) {
      return;
    }
    const data = event.data as
      | { type?: unknown; callForm?: unknown; formContext?: unknown; nonce?: unknown }
      | null;
    if (!data || data.type !== PendingLaunchService.LAUNCH_MESSAGE_TYPE) {
      return;
    }
    if (typeof data.callForm !== 'string') {
      return;
    }
    const formContext = typeof data.formContext === 'string' ? data.formContext : null;
    this.launchForm(data.callForm, formContext);

    // ACK back to V4 so its `window.opener` fallback (v2-starter flow) knows we
    // handled the launch in place and does NOT need to fall back to a
    // full-reload window.open. Only sent when V4 tagged the message with a
    // nonce (the opener path); the retained-handle path omits it.
    if (typeof data.nonce === 'string' && this.isWindowProxy(event.source)) {
      event.source.postMessage(
        { type: PendingLaunchService.LAUNCH_ACK_TYPE, nonce: data.nonce },
        event.origin,
      );
    }
  };

  private isWindowProxy(source: MessageEventSource | null): source is WindowProxy {
    return !!source && 'closed' in source;
  }

  /** Origin of the configured V4 frontend (`app-config.v4FrontendUrl`), or null
   *  when it's unset/unparseable — in which case launch messages are ignored. */
  private v4FrontendOrigin(): string | null {
    const url = this.appConfig?.v4FrontendUrl;
    if (!url) {
      return null;
    }
    try {
      return new URL(url).origin;
    } catch {
      return null;
    }
  }

  /**
   * Launch a form into the running Frames runtime (no reload). Used by the
   * message path. Waits for login + Frames readiness + overlay-clear (these
   * are already satisfied for an open tab, so they resolve immediately), then
   * — UNLIKE the boot path — AWAITS the `formContext` side-channel before
   * dispatching `callmenu`. Awaiting is correct here because Frames is already
   * live: there's no bootstrap delay to mask the side-channel latency, so we
   * must ensure Globals are populated before the form's first server request.
   *
   * `callForm` is validated against the same XSS-safe regex as `capture()`;
   * an invalid value is a no-op.
   */
  launchForm(callForm: string, formContext?: string | null): void {
    if (!/^[A-Za-z0-9_]+$/.test(callForm)) {
      return;
    }
    const context = this.sanitizeFormContext(formContext);

    // Supersede any previous in-flight message-driven launch.
    this.launchSubscription?.unsubscribe();
    this.launchSubscription = combineLatest([this.authService.framesUser$, this.framesService.ready$])
      .pipe(
        filter(([user, ready]) => Boolean(user) && ready),
        take(1),
        switchMap(() => this.waitForOverlayCleared()),
        switchMap(() => (context ? this.formContextSideChannel$(context) : of(null))),
      )
      .subscribe(() => this.dispatchCallmenu(callForm));
  }

  private dispatch(): void {
    if (this.dispatched || !this.pendingFormName) {
      return;
    }
    // eslint-disable-next-line no-console
    console.log(`Dispatching pending launch: form=${this.pendingFormName} context=${this.pendingFormContext}`);
    this.dispatched = true;
    const formName = this.pendingFormName;
    this.pendingFormName = null;
    this.pendingFormContext = null;

    // Synchronous dispatch — the side-channel (if any) was already kicked
    // off in `capture()` and is racing Frames' bootstrap. Calling
    // `callmenu` here keeps it inside the Frames lifecycle window where
    // it actually installs the form view. See the class-level doc for
    // the failure mode of awaiting the side-channel response here.
    this.dispatchCallmenu(formName);
  }

  /**
   * Dispatch `callmenu`, then confirm Frames issued the CALL_FORM request for
   * the form and retry if it didn't.
   *
   * `ready$` latches on Frames' FIRST `viewready` (see `FramesService`), which
   * on the first-login path (Auth0 redirect → DB login → workspace transition)
   * can open the readiness gate before the workspace main-task can accept a
   * `CALL_FORM`. The call is then silently dropped ("callmenu fires, no XHR")
   * and, being one-shot, never retried — login succeeds but the form never
   * opens. Subsequent launches have no redirect/transition, so the same call
   * lands.
   *
   * Fix: fire the first `callmenu` synchronously (the timing that already works
   * on the no-redirect path), then retry every
   * {@link DISPATCH_RETRY_INTERVAL_MS} until Frames emits the CALL_FORM server
   * request for THIS form on `FramesService.request$`, or the
   * {@link DISPATCH_MAX_ATTEMPTS} budget is spent. `callmenu` is idempotent, so
   * re-issuing after the form has opened is a harmless no-op.
   *
   * The stop condition is FORM-SPECIFIC on purpose. Earlier attempts keyed on
   * `viewready` (fires for Frames' own workspace/menu views too, so it could
   * stop before our form's callmenu landed — the form then never opened) and on
   * `getMainTask()` (the opened form isn't surfaced there, so it never matched
   * and retried to the cap). A successful callmenu produces a CALL_FORM request
   * whose form name lives at `body[0].menu[0]['@item']` — matching that is
   * unambiguous (a dropped callmenu produces no such request; other Frames
   * traffic doesn't carry this form's `@item`). If the request is ever missed
   * we fall back to the attempt budget — the form still opens, we just don't
   * stop early.
   */
  private dispatchCallmenu(formName: string): void {
    // eslint-disable-next-line no-console
    console.log(`Calling Frames callmenu for form ${formName}`);

    let landed = false;
    this.dispatchRequestSub?.unsubscribe();
    this.dispatchRequestSub = this.framesService.request$
      .pipe(
        filter((req) => this.isCallFormRequestFor(req, formName)),
        take(1),
      )
      .subscribe(() => {
        landed = true;
      });

    // First attempt — synchronous, matching the timing that works today.
    this.callmenuOnce(formName);

    let attempts = 1;
    this.retrySubscription?.unsubscribe();
    this.retrySubscription = timer(
      PendingLaunchService.DISPATCH_RETRY_INTERVAL_MS,
      PendingLaunchService.DISPATCH_RETRY_INTERVAL_MS,
    )
      .pipe(takeWhile(() => !landed && attempts < PendingLaunchService.DISPATCH_MAX_ATTEMPTS))
      .subscribe({
        next: () => {
          attempts += 1;
          // eslint-disable-next-line no-console
          console.log(`PendingLaunchService: callmenu retry ${attempts} for form ${formName} (no CALL_FORM request yet)`);
          this.callmenuOnce(formName);
        },
        complete: () => {
          this.dispatchRequestSub?.unsubscribe();
          this.dispatchRequestSub = undefined;
          if (!landed) {
            // eslint-disable-next-line no-console
            console.warn(
              `PendingLaunchService: form ${formName} did not open after ${attempts} callmenu attempt(s)`,
            );
          }
        },
      });
  }

  /**
   * Whether `req` is the Frames CALL_FORM request for `formName`. Frames places
   * the launched form's name at `body[0].menu[0]['@item']`; a dropped callmenu
   * produces no such request, and other Frames traffic doesn't carry this
   * form's `@item`, so this is an unambiguous "the form launch landed" signal.
   */
  private isCallFormRequestFor(req: unknown, formName: string): boolean {
    try {
      const item = (req as { body?: Array<{ menu?: Array<{ ['@item']?: unknown }> }> })?.body?.[0]?.menu?.[0]?.[
        '@item'
      ];
      return typeof item === 'string' && item.toUpperCase() === formName.toUpperCase();
    } catch {
      return false;
    }
  }

  /** Single `callmenu` invocation. Re-reads the main-task each call so a retry
   *  targets the current task (it may change during the login transition). */
  private callmenuOnce(formName: string): void {
    const app = window.Frames?.Application;
    const mainTask = app?.getMainTask?.();
    if (!app || !mainTask) {
      // Readiness gates fired but the runtime is in an unexpected state —
      // leave the user on the workspace rather than throwing into the void.
      // eslint-disable-next-line no-console
      console.error('PendingLaunchService: Frames runtime not in expected state, cannot dispatch callmenu');
      return;
    }

    try {
      app.callmenu(mainTask, 'CALL_FORM', formName, mainTask.name, 'Context', '$MAIN$_BLOCK', 'MENU_TREE');
    } catch (e) {
      // Frames sometimes throws when called from an unexpected lifecycle
      // state; we don't want one bad launch to take down the SPA.
      // eslint-disable-next-line no-console
      console.error('PendingLaunchService: callmenu failed', e);
    }
  }
}
